import axios from 'axios';
import apiClient from '@/services/apiClient';
import { withApiError } from '@/services/apiError';
import type {
  AuthResponse,
  LoginRequest,
  LoginResponseData,
  PasswordPolicy,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  RegisterRequest,
  User,
} from '../types';

async function fetchMe(accessToken: string): Promise<User> {
  const response = await axios.get(`${apiClient.defaults.baseURL}/api/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.data ?? response.data;
}

async function buildAuthResponse(responseData: LoginResponseData): Promise<AuthResponse> {
  const { access, refresh, must_change_password, password_changed_at } = responseData;
  const user = await fetchMe(access);
  return {
    tokens: { access, refresh },
    user,
    mustChangePassword: Boolean(must_change_password),
    passwordChangedAt: password_changed_at ?? null,
  };
}

function postLogin(
  url: string,
  payload: Record<string, unknown>,
  errorKey: string,
): Promise<AuthResponse> {
  return withApiError(errorKey, async () => {
    const response = await apiClient.post(url, payload);
    return buildAuthResponse(response.data);
  });
}

export async function registerAccount(data: RegisterRequest): Promise<void> {
  await withApiError('auth.apiErrors.register', async () => {
    const { password2, ...rest } = data;
    const requestData = { ...rest, password_confirm: password2 };
    await apiClient.post('/api/auth/register', requestData);
  });
}

export function loginWithPassword(data: LoginRequest): Promise<AuthResponse> {
  return postLogin('/api/auth/login', { ...data }, 'auth.apiErrors.login');
}

export function loginWithGoogle(
  credential: string,
): Promise<AuthResponse & { is_new_user?: boolean }> {
  return postLogin('/api/auth/google', { token: credential }, 'auth.apiErrors.googleLogin');
}

export async function logoutSession(): Promise<void> {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      await apiClient.post('/api/auth/logout', { refresh: refreshToken });
    }
  } catch {
    // Logout API call failed - caller still clears local state
  }
}

export async function fetchProfile(): Promise<User> {
  return withApiError('auth.apiErrors.getProfile', async () => {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  });
}

export async function requestPasswordReset(
  data: PasswordResetRequest,
): Promise<{ message: string }> {
  return withApiError('auth.apiErrors.requestPasswordReset', async () => {
    const response = await apiClient.post('/api/auth/password-reset', data);
    return response.data;
  });
}

export async function confirmPasswordReset(
  data: PasswordResetConfirmRequest,
): Promise<{ message: string }> {
  return withApiError('auth.apiErrors.confirmPasswordReset', async () => {
    const response = await apiClient.post('/api/auth/password-reset/confirm', data);
    return response.data;
  });
}

export async function requestEmailChange(newEmail: string): Promise<void> {
  await withApiError('auth.apiErrors.requestEmailChange', async () => {
    await apiClient.post('/api/auth/email/change', { new_email: newEmail });
  });
}

export async function confirmEmailChange(token: string): Promise<void> {
  await withApiError('auth.apiErrors.confirmEmailChange', async () => {
    await apiClient.post('/api/auth/email/change/confirm', { token });
  });
}

export async function getPasswordPolicy(): Promise<PasswordPolicy> {
  return withApiError('auth.apiErrors.passwordPolicy', async () => {
    const response = await apiClient.get('/api/auth/password-policy');
    return response.data;
  });
}
